// CashFlowPage.js
// Unified cash flow view with tabs for cash flow, checks management, and recurring transactions

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Switch,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  CardActionArea,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentMethodBreakdown from './components/PaymentMethodBreakdown';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { Link, useSearchParams } from 'react-router-dom';
import { startOfMonth, endOfMonth, format, parseISO, differenceInDays } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import GeneralIncomeDialog from './dialogs/GeneralIncomeDialog';
import GeneralExpenseDialog from './dialogs/GeneralExpenseDialog';
import RecurringTransactionDialog from './dialogs/RecurringTransactionDialog';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog';
import QuickCommandsDialog from './dialogs/QuickCommandsDialog';
import MobileDialog from './dialogs/MobileDialog';
import CashFlowFilterPanel from '../components/CashFlowFilterPanel';
import { useIsPhone } from '../hooks/useIsMobile';
import { useUser } from '../contexts/UserContext';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CashDeskTabContent from './components/CashDeskTabContent';
import ResponsiveToolbar, { ToolbarActions } from '../components/ResponsiveToolbar';
import {
  getCashFlow,
  getChecks,
  getRecurringTransactions,
  toggleRecurringTransaction,
  deleteRecurringTransaction,
  updateCheckStatus,
  updateGeneralTransaction,
  deleteGeneralTransaction,
} from '../services/cashflowService';

// Tab Panel Component — lazy-mount: once mounted, stays mounted (hidden via display)
function TabPanel({ children, value, index, ...other }) {
  const [hasMounted, setHasMounted] = React.useState(value === index);
  React.useEffect(() => {
    if (value === index && !hasMounted) setHasMounted(true);
  }, [value, index, hasMounted]);

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cashflow-tabpanel-${index}`}
      {...other}
    >
      {hasMounted && (
        <Box sx={{ pt: 2, display: value === index ? 'block' : 'none' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Summary Card Component
// foreignAmounts: { USD: { amount: 500, ils: 1582 } } — foreign is primary, ILS hint next to it
// onToggle/expanded: renders the card as a button that opens a breakdown panel below
function SummaryCard({ title, value, color = 'text.primary', foreignAmounts, hint, onToggle, expanded = false }) {
  const ilsFormatted = new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
  const content = (
      <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '1.1rem' }, mb: 0.5, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
          {ilsFormatted}
        </Typography>
        {foreignAmounts && Object.entries(foreignAmounts).map(([curr, data]) => {
          const foreignAmt = typeof data === 'object' ? data.amount : data;
          const ilsAmt = typeof data === 'object' ? data.ils : null;
          if (!foreignAmt || foreignAmt === 0) return null;
          return (
            <Box key={curr} sx={{ mt: 0.5 }}>
              <Typography sx={{ color, fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: curr, minimumFractionDigits: 2 }).format(foreignAmt)}
              </Typography>
              {ilsAmt != null && (
                <Typography variant="body2" color="text.secondary">
                  ≈ {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0 }).format(ilsAmt)}
                </Typography>
              )}
            </Box>
          );
        })}
        {hint && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {hint}
          </Typography>
        )}
        {onToggle && (
          <Box
            sx={{
              mt: 0.75,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              color: expanded ? color : 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              fontWeight: 500,
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>לפי אמצעי תשלום</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>פירוט</Box>
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transition: 'transform 200ms', transform: expanded ? 'rotate(180deg)' : 'none' }}
            />
          </Box>
        )}
      </CardContent>
  );
  return (
    <Card
      sx={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        outline: expanded ? 2 : 0,
        outlineColor: color,
        outlineOffset: -2,
      }}
    >
      {onToggle ? (
        <CardActionArea
          onClick={onToggle}
          aria-expanded={expanded}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          {content}
        </CardActionArea>
      ) : content}
    </Card>
  );
}

const CashFlowPage = () => {
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state - persist in URL
  const activeTab = parseInt(searchParams.get('tab') || '0', 10);
  const setActiveTab = (newTab) => {
    setSearchParams({ tab: newTab.toString() }, { replace: true });
  };

  // Cash Flow filters
  const [cfStartDate, setCfStartDate] = useState(startOfMonth(new Date()));
  const [cfEndDate, setCfEndDate] = useState(endOfMonth(new Date()));
  const [cfTypeFilter, setCfTypeFilter] = useState('all');
  const [cfDirectionFilter, setCfDirectionFilter] = useState('all');
  const [cfPage, setCfPage] = useState(0); // 0-based for MUI TablePagination
  const [cfRowsPerPage, setCfRowsPerPage] = useState(50);

  // Advanced search filters
  const defaultAdvancedFilters = {
    search: '',
    amountMin: '',
    amountMax: '',
    tagId: '',
    paymentMethod: '',
    startDate: null,
    endDate: null,
  };
  const [cfAdvancedFilters, setCfAdvancedFilters] = useState(defaultAdvancedFilters);

  // Payment-method drill-down from the in/out summary cards.
  // breakdownMenu: null | { direction: 'in' | 'out', anchorEl }; cfMethodFilter: null | { direction, method, label }
  const [breakdownMenu, setBreakdownMenu] = useState(null);
  const [cfMethodFilter, setCfMethodFilter] = useState(null);

  // Checks filters
  const [checkStartDate, setCheckStartDate] = useState(startOfMonth(new Date()));
  const [checkEndDate, setCheckEndDate] = useState(endOfMonth(new Date()));
  const [checkStatusFilter, setCheckStatusFilter] = useState('all');
  const [checkDirectionFilter, setCheckDirectionFilter] = useState('all');
  const [checkSearch, setCheckSearch] = useState('');

  // Check search popover
  const [checkSearchPopoverAnchor, setCheckSearchPopoverAnchor] = useState(null);
  const [checkSearchNumber, setCheckSearchNumber] = useState('');
  const [checkSearchAccountNumber, setCheckSearchAccountNumber] = useState('');
  const [checkSearchActive, setCheckSearchActive] = useState(false);

  // Dialog states
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [editRecurringData, setEditRecurringData] = useState(null);
  const [deleteRecurringId, setDeleteRecurringId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Check status edit dialog
  const [showCheckStatusDialog, setShowCheckStatusDialog] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [newCheckStatus, setNewCheckStatus] = useState('');
  const [isUpdatingCheckStatus, setIsUpdatingCheckStatus] = useState(false);

  // General transaction edit dialog
  const [showEditGeneralDialog, setShowEditGeneralDialog] = useState(false);
  const [selectedGeneralTransaction, setSelectedGeneralTransaction] = useState(null);
  const [editGeneralDate, setEditGeneralDate] = useState(null);
  const [editGeneralAmount, setEditGeneralAmount] = useState('');
  const [isUpdatingGeneral, setIsUpdatingGeneral] = useState(false);
  const [showDeleteGeneralDialog, setShowDeleteGeneralDialog] = useState(false);
  const [deleteGeneralId, setDeleteGeneralId] = useState(null);

  // Quick Commands dialog state
  const [showQuickCommandsDialog, setShowQuickCommandsDialog] = useState(false);

  // Quick Commands enabled from ui_config
  const cashFlowPageConfig = userProfile?.ui_config?.pages_configs?.CashFlowPage || {};
  const quickCommandsEnabled = cashFlowPageConfig.quick_commands_enabled || false;
  const cashdeskEnabled = cashFlowPageConfig.cashdesk_enabled || false;

  // Check if advanced filters are active
  const hasAdvancedFilters = Boolean(
    cfAdvancedFilters.search ||
    cfAdvancedFilters.amountMin ||
    cfAdvancedFilters.amountMax ||
    cfAdvancedFilters.tagId ||
    cfAdvancedFilters.paymentMethod ||
    cfAdvancedFilters.startDate ||
    cfAdvancedFilters.endDate
  );

  // Check if date range exceeds 180 days (only matters when no advanced filters)
  const MAX_DATE_RANGE_DAYS = 180;
  const dateRangeExceeded = !hasAdvancedFilters && cfStartDate && cfEndDate &&
    differenceInDays(cfEndDate, cfStartDate) > MAX_DATE_RANGE_DAYS;

  // Fetch cash flow data
  // When advanced filters are active, bypass date/type/direction filters (but use advanced dates if provided)
  const { data: cashFlowData, isLoading: isLoadingCashFlow, isFetching: isFetchingCashFlow } = useQuery({
    queryKey: ['cashflow', cfStartDate, cfEndDate, cfTypeFilter, cfDirectionFilter, cfPage, cfRowsPerPage, cfAdvancedFilters, hasAdvancedFilters, cfMethodFilter],
    queryFn: () => getCashFlow({
      // Use advanced filter dates if provided, otherwise use normal dates (unless other advanced filters are active)
      start_date: hasAdvancedFilters
        ? (cfAdvancedFilters.startDate ? format(cfAdvancedFilters.startDate, 'yyyy-MM-dd') : undefined)
        : format(cfStartDate, 'yyyy-MM-dd'),
      end_date: hasAdvancedFilters
        ? (cfAdvancedFilters.endDate ? format(cfAdvancedFilters.endDate, 'yyyy-MM-dd') : undefined)
        : format(cfEndDate, 'yyyy-MM-dd'),
      // Only apply type/direction when no advanced filters
      type: hasAdvancedFilters ? undefined : (cfTypeFilter !== 'all' ? cfTypeFilter : undefined),
      // A card drill-down pins the direction it was opened from (cash IN ≠ cash OUT)
      direction: cfMethodFilter
        ? cfMethodFilter.direction
        : (hasAdvancedFilters ? undefined : (cfDirectionFilter !== 'all' ? cfDirectionFilter : undefined)),
      page: cfPage + 1, // Backend uses 1-based, MUI uses 0-based
      page_size: cfRowsPerPage,
      // Advanced filters
      search: cfAdvancedFilters.search || undefined,
      amount_min: cfAdvancedFilters.amountMin || undefined,
      amount_max: cfAdvancedFilters.amountMax || undefined,
      tag_id: cfAdvancedFilters.tagId || undefined,
      payment_method: cfMethodFilter?.method || cfAdvancedFilters.paymentMethod || undefined,
    }),
    enabled: activeTab === 0 && !dateRangeExceeded, // Don't fetch if date range exceeds limit
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData, // Keep previous data while fetching to prevent flicker
  });

  // Reset page when filters change
  const handleCfFilterChange = (setter) => (value) => {
    setter(value);
    setCfPage(0); // Reset to first page
  };

  const handleOpenBreakdown = (direction) => (event) => {
    setBreakdownMenu({ direction, anchorEl: event.currentTarget });
  };

  const handleSelectMethod = (row) => {
    const direction = breakdownMenu?.direction;
    setCfMethodFilter(row && direction ? { direction, method: row.method, label: row.label } : null);
    setCfPage(0);
    setBreakdownMenu(null);
  };

  // Handle advanced filter changes
  const handleAdvancedFilterApply = (newFilters) => {
    setCfAdvancedFilters(newFilters);
    setCfPage(0); // Reset to first page
  };

  // Fetch checks data
  const { data: checksData, isLoading: isLoadingChecks } = useQuery({
    queryKey: ['cashflow-checks', checkStartDate, checkEndDate, checkStatusFilter, checkDirectionFilter, checkSearch],
    queryFn: () => getChecks({
      start_date: format(checkStartDate, 'yyyy-MM-dd'),
      end_date: format(checkEndDate, 'yyyy-MM-dd'),
      status: checkStatusFilter !== 'all' ? checkStatusFilter : undefined,
      direction: checkDirectionFilter !== 'all' ? checkDirectionFilter : undefined,
      search: checkSearch || undefined,
    }),
    enabled: activeTab === 1,
    staleTime: 30 * 1000,
  });

  // Fetch recurring transactions
  const { data: recurringData, isLoading: isLoadingRecurring } = useQuery({
    queryKey: ['recurring-transactions'],
    queryFn: getRecurringTransactions,
    enabled: activeTab === 2,
    staleTime: 60 * 1000,
  });

  // Handle recurring toggle
  const handleToggleRecurring = async (recurringId, currentStatus) => {
    try {
      await toggleRecurringTransaction({
        recurring_id: recurringId,
        is_active: !currentStatus,
      });
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
    } catch (err) {
      console.error('Error toggling recurring transaction:', err);
    }
  };

  // Handle recurring edit
  const handleEditRecurring = (recurring) => {
    setEditRecurringData(recurring);
    setShowRecurringDialog(true);
  };

  // Handle recurring delete
  const handleDeleteRecurring = (recurringId) => {
    setDeleteRecurringId(recurringId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteRecurring = async () => {
    try {
      await deleteRecurringTransaction(deleteRecurringId);
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      setShowDeleteDialog(false);
      setDeleteRecurringId(null);
    } catch (err) {
      throw err;
    }
  };

  // Handle check status edit
  const handleEditCheckStatus = (check) => {
    setSelectedCheck(check);
    setNewCheckStatus(check.status);
    setShowCheckStatusDialog(true);
  };

  const handleUpdateCheckStatus = async () => {
    if (!selectedCheck || !newCheckStatus) return;

    setIsUpdatingCheckStatus(true);
    try {
      await updateCheckStatus(
        selectedCheck.reference_type,
        selectedCheck.reference_id,
        newCheckStatus
      );
      queryClient.refetchQueries({ queryKey: ['cashflow-checks'] });
      setShowCheckStatusDialog(false);
      setSelectedCheck(null);
      setNewCheckStatus('');
    } catch (err) {
      console.error('Error updating check status:', err);
    } finally {
      setIsUpdatingCheckStatus(false);
    }
  };

  // General transaction edit handlers
  const handleEditGeneralTransaction = (item) => {
    setSelectedGeneralTransaction(item);
    setEditGeneralDate(new Date(item.date));
    setEditGeneralAmount(item.amount_in || item.amount_out || '');
    setShowEditGeneralDialog(true);
  };

  const handleUpdateGeneralTransaction = async () => {
    if (!selectedGeneralTransaction || !editGeneralAmount) return;

    setIsUpdatingGeneral(true);
    try {
      const payload = {
        transaction_id: selectedGeneralTransaction.reference_id,
        actual_date: editGeneralDate.toISOString(),
        amount: parseFloat(editGeneralAmount),
      };

      // If there's a payment_method_id, include it for granular update
      // This ensures only that specific payment method is updated
      if (selectedGeneralTransaction.payment_method_id) {
        payload.payment_method_id = selectedGeneralTransaction.payment_method_id;
      }

      await updateGeneralTransaction(payload);
      queryClient.refetchQueries({ queryKey: ['cashflow'] });
      setShowEditGeneralDialog(false);
      setSelectedGeneralTransaction(null);
      setEditGeneralDate(null);
      setEditGeneralAmount('');
    } catch (err) {
      console.error('Error updating general transaction:', err);
    } finally {
      setIsUpdatingGeneral(false);
    }
  };

  const handleDeleteGeneralTransaction = (transactionId, paymentMethodId = null) => {
    // Store both transaction_id and payment_method_id for granular deletion
    setDeleteGeneralId({ transactionId, paymentMethodId });
    setShowDeleteGeneralDialog(true);
  };

  const confirmDeleteGeneralTransaction = async (password) => {
    try {
      const { transactionId, paymentMethodId } = deleteGeneralId;
      await deleteGeneralTransaction(transactionId, password, paymentMethodId);
      queryClient.refetchQueries({ queryKey: ['cashflow'] });
      setShowDeleteGeneralDialog(false);
      setDeleteGeneralId(null);
    } catch (err) {
      throw err;
    }
  };

  // Check search popover handlers
  const handleCheckSearchOpen = (event) => {
    setCheckSearchPopoverAnchor(event.currentTarget);
  };

  const handleCheckSearchClose = () => {
    setCheckSearchPopoverAnchor(null);
  };

  const handleCheckSearchExecute = () => {
    // Build search string from fields
    const searchParts = [];
    if (checkSearchNumber.trim()) searchParts.push(checkSearchNumber.trim());
    if (checkSearchAccountNumber.trim()) searchParts.push(checkSearchAccountNumber.trim());

    setCheckSearch(searchParts.join(' '));
    setCheckSearchActive(searchParts.length > 0);
    setCheckSearchPopoverAnchor(null);
  };

  const handleCheckSearchClear = () => {
    setCheckSearchNumber('');
    setCheckSearchAccountNumber('');
    setCheckSearch('');
    setCheckSearchActive(false);
  };

  // Type config with labels and colors
  const typeConfig = {
    client_payment: { label: 'תשלום מלקוח', color: 'primary', variant: 'outlined' },
    supplier_payment: { label: 'תשלום לספק', color: 'secondary', variant: 'outlined' },
    general_income: { label: 'הכנסה כללית', color: 'success', variant: 'filled' },
    general_expense: { label: 'הוצאה כללית', color: 'error', variant: 'filled' },
    client_charge: { label: 'חיוב לקוח', color: 'info', variant: 'outlined' },
    supplier_purchase: { label: 'רכישה מספק', color: 'warning', variant: 'outlined' },
  };

  // Type labels (for backwards compatibility)
  const typeLabels = {
    client_payment: 'תשלום מלקוח',
    supplier_payment: 'תשלום לספק',
    general_income: 'הכנסה כללית',
    general_expense: 'הוצאה כללית',
  };

  // Payment method labels
  const paymentMethodLabels = {
    cash: 'מזומן',
    check: 'צ\'ק',
    credit_card: 'כרטיס אשראי',
    bank_transfer: 'העברה בנקאית',
    other: 'אחר',
    unknown: 'לא צוין',
  };

  // Check status labels and colors
  const checkStatusConfig = {
    pending: { label: 'ממתין', color: 'warning' },
    cleared: { label: 'נפרע', color: 'success' },
    bounced: { label: 'חזר', color: 'error' },
    cancelled: { label: 'בוטל', color: 'default' },
  };

  // Frequency labels
  const frequencyLabels = {
    weekly: 'שבועי',
    monthly: 'חודשי',
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Typography variant="h4" sx={{ mb: { xs: 1.5, sm: 3 }, fontWeight: 'bold' }}>
          תזרים מזומנים
        </Typography>

        {/* Tabs */}
        <Paper sx={{ mb: { xs: 1.5, sm: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant={isPhone ? 'scrollable' : 'fullWidth'}
            scrollButtons={isPhone ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.8rem', sm: '1rem' },
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 1.5, sm: 2 },
              },
            }}
          >
            <Tab label="תזרים מזומנים" />
            <Tab label="ניהול צ'קים" />
            <Tab label="הוצאות ו הכנסות קבועות" />
            {cashdeskEnabled && <Tab label="קופות" />}
          </Tabs>
        </Paper>

        {/* Tab 0: Cash Flow */}
        <TabPanel value={activeTab} index={0}>
          {/* Filters and Actions */}
          <ResponsiveToolbar>
            <DatePicker
              label="חודש"
              value={cfStartDate}
              onChange={(newValue) => {
                if (newValue) {
                  handleCfFilterChange(setCfStartDate)(startOfMonth(newValue));
                  setCfEndDate(endOfMonth(newValue));
                }
              }}
              views={['month', 'year']}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={hasAdvancedFilters}
            />
            <DatePicker
              label="מתאריך"
              value={cfStartDate}
              onChange={(newValue) => handleCfFilterChange(setCfStartDate)(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={hasAdvancedFilters}
            />
            <DatePicker
              label="עד תאריך"
              value={cfEndDate}
              onChange={(newValue) => handleCfFilterChange(setCfEndDate)(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={hasAdvancedFilters}
            />
            <FormControl size="small" disabled={hasAdvancedFilters} sx={{ minWidth: { xs: 0, md: 140 } }}>
              <InputLabel>סוג</InputLabel>
              <Select
                value={cfTypeFilter}
                onChange={(e) => handleCfFilterChange(setCfTypeFilter)(e.target.value)}
                label="סוג"
              >
                <MenuItem value="all">הכל</MenuItem>
                <MenuItem value="client">לקוחות</MenuItem>
                <MenuItem value="supplier">ספקים</MenuItem>
                <MenuItem value="general">כללי</MenuItem>
                <MenuItem value="obligation">התחייבויות</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" disabled={hasAdvancedFilters} sx={{ minWidth: { xs: 0, md: 140 } }}>
              <InputLabel>כיוון</InputLabel>
              <Select
                value={cfDirectionFilter}
                onChange={(e) => handleCfFilterChange(setCfDirectionFilter)(e.target.value)}
                label="כיוון"
              >
                <MenuItem value="all">הכל</MenuItem>
                <MenuItem value="in">נכנס</MenuItem>
                <MenuItem value="out">יוצא</MenuItem>
              </Select>
            </FormControl>
            <ToolbarActions>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => setShowIncomeDialog(true)}
                sx={{direction: 'ltr'}}
              >
                הכנסה כללית
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RemoveIcon />}
                onClick={() => setShowExpenseDialog(true)}
                sx={{direction: 'ltr'}}
              >
                הוצאה כללית
              </Button>
              {quickCommandsEnabled && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FlashOnIcon />}
                  onClick={() => setShowQuickCommandsDialog(true)}
                  sx={{ direction: 'ltr' }}
                >
                  פקודות מהירות
                </Button>
              )}
            </ToolbarActions>
          </ResponsiveToolbar>

          {/* Advanced Filter Panel */}
          <CashFlowFilterPanel
            onApply={handleAdvancedFilterApply}
            appliedFilters={cfAdvancedFilters}
          />

          {/* Date Range Warning */}
          {dateRangeExceeded && (
            <Alert severity="error" sx={{ mb: 2 }}>
              טווח התאריכים חורג מ-180 יום. אנא בחר טווח תאריכים קצר יותר.
            </Alert>
          )}

          {/* Summary Cards - Cash Flow (ILS + foreign in same card) */}
          {(() => {
            const bc = (!dateRangeExceeded && cashFlowData?.summary?.by_currency) || {};
            const foreignIn = {};
            const foreignOut = {};
            const foreignPurchases = {};
            let foreignOutHints = [];
            Object.entries(bc).forEach(([curr, data]) => {
              if (data.total_in) foreignIn[curr] = data.total_in;
              if (data.total_out) foreignOut[curr] = { amount: data.total_out, ils: data.total_out_ils || null };
              if (data.total_purchases) foreignPurchases[curr] = { amount: data.total_purchases, ils: data.total_purchases_ils || null };
              if (data.total_out) foreignOutHints.push(`${new Intl.NumberFormat('he-IL', { style: 'currency', currency: curr, minimumFractionDigits: 0 }).format(data.total_out)}`);
            });
            return (
              <>
                <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2, alignItems: 'stretch' }}>
                  <Grid item xs={4}>
                    <SummaryCard
                      title="נכנס"
                      value={dateRangeExceeded ? 0 : cashFlowData?.summary?.total_in}
                      color="success.main"
                      foreignAmounts={Object.keys(foreignIn).length ? foreignIn : null}
                      onToggle={handleOpenBreakdown('in')}
                      expanded={breakdownMenu?.direction === 'in'}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SummaryCard
                      title="יוצא"
                      value={dateRangeExceeded ? 0 : cashFlowData?.summary?.total_out}
                      color="error.main"
                      foreignAmounts={Object.keys(foreignOut).length ? foreignOut : null}
                      onToggle={handleOpenBreakdown('out')}
                      expanded={breakdownMenu?.direction === 'out'}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SummaryCard
                      title="נטו"
                      value={dateRangeExceeded ? 0 : (cashFlowData?.summary?.combined_net ?? cashFlowData?.summary?.net)}
                      color={(dateRangeExceeded ? 0 : (cashFlowData?.summary?.combined_net ?? cashFlowData?.summary?.net)) >= 0 ? 'success.main' : 'error.main'}
                      hint={foreignOutHints.length ? `כולל ${foreignOutHints.join(', ')} הוצאות` : null}
                    />
                  </Grid>
                </Grid>

                {/* Payment-method breakdown (drop-down under the in/out cards) */}
                <PaymentMethodBreakdown
                  anchorEl={breakdownMenu?.anchorEl}
                  open={Boolean(breakdownMenu) && !dateRangeExceeded}
                  onClose={() => setBreakdownMenu(null)}
                  rows={(breakdownMenu?.direction === 'out'
                    ? cashFlowData?.summary?.out_by_method
                    : cashFlowData?.summary?.in_by_method) || []}
                  selectedMethod={cfMethodFilter?.direction === breakdownMenu?.direction ? cfMethodFilter?.method : null}
                  onSelect={handleSelectMethod}
                />

                {/* Summary Cards - Obligations */}
                <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2, alignItems: 'stretch' }}>
                  <Grid item xs={6}>
                    <SummaryCard
                      title="מכירות ללקוחות"
                      value={dateRangeExceeded ? 0 : cashFlowData?.summary?.total_sales}
                      color="info.main"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SummaryCard
                      title="קנייה מספקים"
                      value={dateRangeExceeded ? 0 : cashFlowData?.summary?.total_purchases}
                      color="warning.main"
                      foreignAmounts={Object.keys(foreignPurchases).length ? foreignPurchases : null}
                    />
                  </Grid>
                </Grid>
              </>
            );
          })()}

          {cfMethodFilter && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                color="primary"
                label={`מציג רק: ${cfMethodFilter.direction === 'in' ? 'נכנס' : 'יוצא'} · ${cfMethodFilter.label}`}
                onDelete={() => { setCfMethodFilter(null); setCfPage(0); }}
              />
            </Box>
          )}

          {/* Cash Flow Table */}
          {isLoadingCashFlow && !dateRangeExceeded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: { xs: '60vh', sm: 'none' } }}>
                <Table size="medium" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.200' }}>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>תאריך</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>סוג</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' } }}>גורם</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' } }}>קטגוריה</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' } }}>אמצעי תשלום</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' }, color: 'success.main' }}>נכנס</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' }, color: 'error.main' }}>יוצא</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' }, color: 'info.main' }}>התחייבות</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' } }}>הערות</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '1rem' } }}>פעולות</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!dateRangeExceeded && cashFlowData?.items?.length > 0 ? (
                      cashFlowData.items.map((item, index) => (
                        <TableRow
                          key={`${item.reference_type}-${item.reference_id}-${index}`}
                          sx={{
                            backgroundColor: item.amount_in
                              ? 'rgba(76, 175, 80, 0.05)'  // green for income
                              : item.amount_out
                                ? 'rgba(244, 67, 54, 0.05)'  // red for expense
                                : item.obligation_type === 'receivable'
                                  ? 'rgba(33, 150, 243, 0.05)'  // blue for client charge
                                  : 'rgba(255, 152, 0, 0.05)',  // orange for supplier purchase
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                            {new Date(item.date).toLocaleDateString('he-IL')}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={typeConfig[item.type]?.label || item.type}
                              color={typeConfig[item.type]?.color || 'default'}
                              variant={typeConfig[item.type]?.variant || 'outlined'}
                              size="small"
                              sx={{ fontSize: '0.85rem', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                            {(item.type === 'client_payment' || item.type === 'client_charge') ? (
                              <Link
                                to={`/client/${item.source_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: item.type === 'client_charge' ? '#0288d1' : '#1976d2', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}
                              >
                                {item.source}
                              </Link>
                            ) : (item.type === 'supplier_payment' || item.type === 'supplier_purchase') ? (
                              <Link
                                to={`/supplier/${item.source_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: item.type === 'supplier_purchase' ? '#ed6c02' : '#9c27b0', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}
                              >
                                {item.source}
                              </Link>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                            {(item.type === 'general_income' || item.type === 'general_expense') ? (item.source || '-') : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                            {paymentMethodLabels[item.payment_method] || item.payment_method_display || item.payment_method}
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1rem' }}>
                            {item.amount_in ? (
                              item.currency && item.currency !== 'ILS' && item.foreign_amount != null ? (
                                <>{new Intl.NumberFormat('he-IL', { style: 'currency', currency: item.currency }).format(item.foreign_amount)}<br/><Typography variant="caption" color="text.secondary">{`₪${item.amount_in.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}</Typography></>
                              ) : `₪${item.amount_in.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`
                            ) : ''}
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'error.main', fontWeight: 'bold', fontSize: '1rem' }}>
                            {item.amount_out ? (
                              item.currency && item.currency !== 'ILS' && item.foreign_amount != null ? (
                                <>{new Intl.NumberFormat('he-IL', { style: 'currency', currency: item.currency }).format(item.foreign_amount)}<br/><Typography variant="caption" color="text.secondary">{`₪${item.amount_out.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}</Typography></>
                              ) : `₪${item.amount_out.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`
                            ) : ''}
                          </TableCell>
                          <TableCell align="center" sx={{
                            color: item.obligation_type === 'receivable' ? 'info.main' : 'warning.main',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                          }}>
                            {item.amount_obligation ? (
                              item.currency && item.currency !== 'ILS' && item.foreign_amount != null ? (
                                <>{new Intl.NumberFormat('he-IL', { style: 'currency', currency: item.currency }).format(item.foreign_amount)}<br/><Typography variant="caption" color="text.secondary">{`₪${item.amount_obligation.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}</Typography></>
                              ) : `₪${item.amount_obligation.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`
                            ) : ''}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.95rem', maxWidth: 200 }}>
                            {(item.type === 'general_income' || item.type === 'general_expense') && item.note ? (
                              <Tooltip title={item.note} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 180,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {item.note}
                                </Typography>
                              </Tooltip>
                            ) : ''}
                          </TableCell>
                          <TableCell align="center">
                            {(item.type === 'general_income' || item.type === 'general_expense') ? (
                              <>
                                <Tooltip title="ערוך">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditGeneralTransaction(item)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="מחק">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteGeneralTransaction(item.reference_id, item.payment_method_id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary">אין נתונים להצגה</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {isFetchingCashFlow && !isLoadingCashFlow && (
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                )}
                <TablePagination
                  component="div"
                  count={dateRangeExceeded ? 0 : (cashFlowData?.pagination?.total_count || 0)}
                  page={cfPage}
                  onPageChange={(e, newPage) => setCfPage(newPage)}
                  rowsPerPage={cfRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setCfRowsPerPage(parseInt(e.target.value, 10));
                    setCfPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  labelRowsPerPage="שורות בעמוד:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
                  sx={{ flexGrow: 1 }}
                />
              </Box>
            </Paper>
          )}
        </TabPanel>

        {/* Tab 1: Checks Management */}
        <TabPanel value={activeTab} index={1}>
          {/* Filters */}
          <ResponsiveToolbar>
            <Button
              variant={checkSearchActive ? 'contained' : 'outlined'}
              startIcon={<SearchIcon />}
              onClick={handleCheckSearchOpen}
              size="medium"
              color={checkSearchActive ? 'primary' : 'inherit'}
              sx={{direction: 'ltr'}}
            >
              חיפוש
            </Button>
            {checkSearchActive && (
              <Chip
                label={`חיפוש: ${checkSearchNumber || checkSearchAccountNumber}`}
                onDelete={handleCheckSearchClear}
                size="small"
                color="primary"
                sx={{direction: 'ltr'}}
              />
            )}
            <DatePicker
              label="חודש"
              value={checkStartDate}
              onChange={(newValue) => {
                if (newValue) {
                  setCheckStartDate(startOfMonth(newValue));
                  setCheckEndDate(endOfMonth(newValue));
                }
              }}
              views={['month', 'year']}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={checkSearchActive}
            />
            <DatePicker
              label="מתאריך פירעון"
              value={checkStartDate}
              onChange={(newValue) => setCheckStartDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={checkSearchActive}
            />
            <DatePicker
              label="עד תאריך פירעון"
              value={checkEndDate}
              onChange={(newValue) => setCheckEndDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
              disabled={checkSearchActive}
            />
            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 140 } }}>
              <InputLabel>סטטוס</InputLabel>
              <Select
                value={checkStatusFilter}
                onChange={(e) => setCheckStatusFilter(e.target.value)}
                label="סטטוס"
                disabled={checkSearchActive}
              >
                <MenuItem value="all">הכל</MenuItem>
                <MenuItem value="pending">ממתין</MenuItem>
                <MenuItem value="cleared">נפרע</MenuItem>
                <MenuItem value="bounced">חזר</MenuItem>
                <MenuItem value="cancelled">בוטל</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 140 } }}>
              <InputLabel>כיוון</InputLabel>
              <Select
                value={checkDirectionFilter}
                onChange={(e) => setCheckDirectionFilter(e.target.value)}
                label="כיוון"
                disabled={checkSearchActive}
              >
                <MenuItem value="all">הכל</MenuItem>
                <MenuItem value="incoming">נכנס</MenuItem>
                <MenuItem value="outgoing">יוצא</MenuItem>
              </Select>
            </FormControl>
          </ResponsiveToolbar>

          {/* Search Popover */}
          <Popover
            open={Boolean(checkSearchPopoverAnchor)}
            anchorEl={checkSearchPopoverAnchor}
            onClose={handleCheckSearchClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, width: { xs: 'calc(100vw - 32px)', sm: 300 }, maxWidth: '100vw' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                חיפוש צ'קים
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="מספר צ'ק"
                value={checkSearchNumber}
                onChange={(e) => setCheckSearchNumber(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="הזן מספר צ'ק"
              />
              <TextField
                fullWidth
                size="small"
                label="מספר חשבון"
                value={checkSearchAccountNumber}
                onChange={(e) => setCheckSearchAccountNumber(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="הזן מספר חשבון"
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" size="small" onClick={handleCheckSearchClose}>
                  ביטול
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCheckSearchExecute}
                  disabled={!checkSearchNumber.trim() && !checkSearchAccountNumber.trim()}
                  startIcon={<SearchIcon />}
                  sx={{direction: 'ltr'}}
                >
                  חפש
                </Button>
              </Box>
            </Box>
          </Popover>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="צ'קים נכנסים"
                value={checksData?.summary?.incoming_total}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="צ'קים יוצאים"
                value={checksData?.summary?.outgoing_total}
                color="error.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="נטו"
                value={checksData?.summary?.net}
                color={checksData?.summary?.net >= 0 ? 'success.main' : 'error.main'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ minWidth: 120 }}>
                <CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    סטטוס צ'קים
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">ממתין</Typography>
                      <Typography variant="body1" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                        {checksData?.summary?.pending_count || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">נפרע</Typography>
                      <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        {checksData?.summary?.cleared_count || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">חזר</Typography>
                      <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        {checksData?.summary?.bounced_count || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">בוטל</Typography>
                      <Typography variant="body1" sx={{ color: 'text.disabled', fontWeight: 'bold' }}>
                        {checksData?.summary?.cancelled_count || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Checks Table */}
          {isLoadingChecks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.200' }}>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>תאריך פירעון</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>מקור</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>שם</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>מספר צ'ק</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>בנק</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>מספר חשבון</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>סכום</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>כיוון</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>סטטוס</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checksData?.items?.length > 0 ? (
                    checksData.items.map((item, index) => (
                      <TableRow
                        key={`check-${item.reference_id}-${index}`}
                        sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                          {new Date(item.due_date).toLocaleDateString('he-IL')}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                          {item.source_type === 'client' ? 'לקוח' :
                           item.source_type === 'supplier' ? 'ספק' : 'כללי'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                          {item.source_type === 'client' && item.source_id ? (
                            <Link
                              to={`/client/${item.source_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}
                            >
                              {item.source_name}
                            </Link>
                          ) : item.source_type === 'supplier' && item.source_id ? (
                            <Link
                              to={`/supplier/${item.source_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#9c27b0', textDecoration: 'none', fontWeight: 600 }}
                            >
                              {item.source_name}
                            </Link>
                          ) : (
                            item.source_name
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>{item.check_number}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>{item.bank_name || '-'}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.95rem' }}>{item.account_number || '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                          {item.currency && item.currency !== 'ILS' && item.foreign_amount != null ? (
                            <>
                              {new Intl.NumberFormat('he-IL', { style: 'currency', currency: item.currency }).format(item.foreign_amount)}
                              <br/>
                              <Typography variant="caption" color="text.secondary">
                                {`₪${item.amount?.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}
                              </Typography>
                            </>
                          ) : (
                            `₪${item.amount?.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.direction === 'incoming' ? 'נכנס' : 'יוצא'}
                            size="small"
                            color={item.direction === 'incoming' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={checkStatusConfig[item.status]?.label || item.status}
                            size="small"
                            color={checkStatusConfig[item.status]?.color || 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="ערוך סטטוס">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditCheckStatus(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="text.secondary">אין צ'קים להצגה</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 2: Recurring Transactions */}
        <TabPanel value={activeTab} index={2}>
          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{direction: 'ltr'}}
              onClick={() => {
                setEditRecurringData(null);
                setShowRecurringDialog(true);
              }}
            >
              הוסף תנועה חוזרת
            </Button>
          </Box>

          {/* Recurring Transactions Table */}
          {isLoadingRecurring ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell>שם</TableCell>
                    <TableCell>סוג</TableCell>
                    <TableCell>קטגוריה</TableCell>
                    <TableCell align="right">סכום</TableCell>
                    <TableCell>תדירות</TableCell>
                    <TableCell>הפעלה הבאה</TableCell>
                    <TableCell>רישום אחרון</TableCell>
                    <TableCell>תאריך סיום</TableCell>
                    <TableCell align="center">פעיל</TableCell>
                    <TableCell align="center">פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recurringData?.length > 0 ? (
                    recurringData.map((item) => (
                      <TableRow
                        key={item.RecurringID}
                        sx={{
                          opacity: item.IsActive ? 1 : 0.5,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <TableCell>{item.Name}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.Type === 'income' ? 'הכנסה' : 'הוצאה'}
                            size="small"
                            color={item.Type === 'income' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{item.TagName || '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ₪{parseFloat(item.Amount).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {frequencyLabels[item.Frequency] || item.Frequency}
                          {item.Frequency === 'monthly' && item.DayOfMonth && ` (יום ${item.DayOfMonth})`}
                        </TableCell>
                        <TableCell>
                          {item.NextRun ? new Date(item.NextRun).toLocaleDateString('he-IL') : '-'}
                        </TableCell>
                        <TableCell>
                          {item.LastGenerated ? new Date(item.LastGenerated).toLocaleDateString('he-IL') : '-'}
                        </TableCell>
                        <TableCell>
                          {item.EndDate ? new Date(item.EndDate).toLocaleDateString('he-IL') : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={item.IsActive}
                            onChange={() => handleToggleRecurring(item.RecurringID, item.IsActive)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="ערוך">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditRecurring(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="מחק">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRecurring(item.RecurringID)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">אין תנועות חוזרות</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 3: Cash Desks (config-gated) */}
        {cashdeskEnabled && (
          <TabPanel value={activeTab} index={3}>
            <CashDeskTabContent />
          </TabPanel>
        )}
      </Box>

      {/* Dialogs */}
      <GeneralIncomeDialog
        open={showIncomeDialog}
        onClose={() => setShowIncomeDialog(false)}
        onSuccess={() => {
          // Refetch immediately to show new data
          queryClient.refetchQueries({ queryKey: ['cashflow'] });
          queryClient.refetchQueries({ queryKey: ['cashflow-summary'] });
        }}
      />

      <GeneralExpenseDialog
        open={showExpenseDialog}
        onClose={() => setShowExpenseDialog(false)}
        onSuccess={() => {
          // Refetch immediately to show new data
          queryClient.refetchQueries({ queryKey: ['cashflow'] });
          queryClient.refetchQueries({ queryKey: ['cashflow-summary'] });
        }}
      />

      <RecurringTransactionDialog
        open={showRecurringDialog}
        onClose={() => {
          setShowRecurringDialog(false);
          setEditRecurringData(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
        }}
        editData={editRecurringData}
      />

      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeleteRecurringId(null);
        }}
        onConfirm={confirmDeleteRecurring}
        title="מחיקת תנועה חוזרת"
        message="האם אתה בטוח שברצונך למחוק תנועה חוזרת זו? פעולה זו אינה ניתנת לביטול."
        requirePassword={false}
      />

      {/* Check Status Edit Dialog */}
      <MobileDialog
        open={showCheckStatusDialog}
        onClose={() => {
          setShowCheckStatusDialog(false);
          setSelectedCheck(null);
          setNewCheckStatus('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>עדכון סטטוס צ'ק</DialogTitle>
        <DialogContent>
          {selectedCheck && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                צ'ק מספר: {selectedCheck.check_number}
                {selectedCheck.source_name && ` | ${selectedCheck.source_name}`}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>סטטוס</InputLabel>
                <Select
                  value={newCheckStatus}
                  onChange={(e) => setNewCheckStatus(e.target.value)}
                  label="סטטוס"
                >
                  <MenuItem value="pending">ממתין</MenuItem>
                  <MenuItem value="cleared">נפרע</MenuItem>
                  <MenuItem value="bounced">חזר</MenuItem>
                  <MenuItem value="cancelled">בוטל</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCheckStatusDialog(false);
              setSelectedCheck(null);
              setNewCheckStatus('');
            }}
            color="inherit"
          >
            ביטול
          </Button>
          <Button
            onClick={handleUpdateCheckStatus}
            variant="contained"
            disabled={isUpdatingCheckStatus || !newCheckStatus}
          >
            {isUpdatingCheckStatus ? <CircularProgress size={24} /> : 'עדכן'}
          </Button>
        </DialogActions>
      </MobileDialog>

      {/* General Transaction Edit Dialog */}
      <MobileDialog
        open={showEditGeneralDialog}
        onClose={() => {
          setShowEditGeneralDialog(false);
          setSelectedGeneralTransaction(null);
          setEditGeneralDate(null);
          setEditGeneralAmount('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>עריכת תנועה</DialogTitle>
        <DialogContent>
          {selectedGeneralTransaction && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedGeneralTransaction.type === 'general_income' ? 'הכנסה כללית' : 'הוצאה כללית'}
                {selectedGeneralTransaction.source && ` - ${selectedGeneralTransaction.source}`}
              </Typography>
              <DatePicker
                label="תאריך"
                value={editGeneralDate}
                onChange={(newValue) => setEditGeneralDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                fullWidth
                label="סכום"
                type="number"
                value={editGeneralAmount}
                onChange={(e) => setEditGeneralAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditGeneralDialog(false);
              setSelectedGeneralTransaction(null);
              setEditGeneralDate(null);
              setEditGeneralAmount('');
            }}
            color="inherit"
          >
            ביטול
          </Button>
          <Button
            onClick={handleUpdateGeneralTransaction}
            variant="contained"
            disabled={isUpdatingGeneral || !editGeneralAmount || !editGeneralDate}
          >
            {isUpdatingGeneral ? <CircularProgress size={24} /> : 'עדכן'}
          </Button>
        </DialogActions>
      </MobileDialog>

      {/* General Transaction Delete Confirmation */}
      <ConfirmDeleteDialog
        open={showDeleteGeneralDialog}
        onClose={() => {
          setShowDeleteGeneralDialog(false);
          setDeleteGeneralId(null);
        }}
        onConfirm={confirmDeleteGeneralTransaction}
        title="מחיקת תנועה"
        message="האם אתה בטוח שברצונך למחוק תנועה זו? פעולה זו אינה ניתנת לביטול."
        requirePassword={true}
      />

      {/* Quick Commands Dialog */}
      {quickCommandsEnabled && (
        <QuickCommandsDialog
          open={showQuickCommandsDialog}
          onClose={() => setShowQuickCommandsDialog(false)}
        />
      )}
    </LocalizationProvider>
  );
};

export default CashFlowPage;
